import { apiClient, idempotentConfig, unwrap } from './client';
import type { ApiEnvelope } from './types';

/**
 * IAM UC2 §9 — identity federation (enterprise IdP brokering). A thin contract-faithful transport
 * over `/v1/federation/bindings` like teamAccess.ts: mutations carry an `ol-idempotency-key`; THE
 * SERVER IS THE GATE (§12 tamper contract). `federation.*` keys are RECOVERY-PLANE (G10) — who may
 * enter through the door is never delegable, so only matrix holders ever succeed here.
 *
 * The G17 pins are FIXED FACTS, not choices, and the wire view carries them read-only:
 *   syncMode    = FORCE     (mappers re-apply every login — a removed enterprise group bites)
 *   jitFloorRole = hub_user  (first broker login lands on the zero-grant floor, never analyst)
 * `claimRoleMap` may never name `platform_admin` (server: UAM_ROLE_FORBIDDEN) nor an unknown role
 * (UAM_ROLE_UNKNOWN — a typo'd mapper would silently grant nobody, or somebody later).
 *
 * The `clientSecret` on create TRANSITS into Keycloak's provider config — it is never stored in a
 * OneLattice table, never logged, never echoed back (the view has no secret field, by design).
 *
 * Server refusals the panel renders: 501 MODE_NOT_IMPLEMENTED (SAML — a named later arrival),
 * 503 provisioning seam absent, 400 missing OIDC endpoints / duplicate ACTIVE alias.
 */

export interface FederationBinding {
  bindingId: string;
  alias: string;
  protocol: 'OIDC' | 'SAML';
  issuer: string;
  /** Always FORCE (G17, DDL CHECK) — surfaced so the console can SAY so, not choose it. */
  syncMode: string;
  /** Always hub_user (G17, DDL CHECK) — the JIT zero-grant floor. */
  jitFloorRole: string;
  trustBrokerAcr: boolean;
  claimRoleMap: Record<string, string> | null;
  claimSpokeMap: Record<string, string> | null;
  status: 'ACTIVE' | 'DISABLED';
  createdBy: string;
  createdAt: string | null;
  disabledBy: string | null;
  disabledAt: string | null;
}

export interface CreateBindingRequest {
  alias: string;
  protocol: 'OIDC' | 'SAML';
  issuer: string;
  authorizationUrl?: string;
  tokenUrl?: string;
  clientId?: string;
  /** Transient — see module doc. Never echoed back. */
  clientSecret?: string;
  roleClaim?: string;
  claimRoleMap?: Record<string, string>;
  spokeClaim?: string;
  claimSpokeMap?: Record<string, string>;
  trustBrokerAcr?: boolean;
}

export async function listBindings(): Promise<FederationBinding[]> {
  const { data } = await apiClient.get<ApiEnvelope<{ bindings: FederationBinding[] }>>(
    '/v1/federation/bindings');
  const body = unwrap(data);
  return Array.isArray(body?.bindings) ? body.bindings : [];
}

export async function createBinding(req: CreateBindingRequest): Promise<FederationBinding> {
  const { data } = await apiClient.post<ApiEnvelope<FederationBinding>>(
    '/v1/federation/bindings', req, idempotentConfig());
  return unwrap(data);
}

/** Disable = Keycloak IdP off + row UPDATE (I7): logins stop, link history stays. */
export async function disableBinding(bindingId: string): Promise<FederationBinding> {
  const { data } = await apiClient.post<ApiEnvelope<FederationBinding>>(
    `/v1/federation/bindings/${encodeURIComponent(bindingId)}/disable`, undefined,
    idempotentConfig());
  return unwrap(data);
}
